const message = (id, defaultMessage) => ({ id, defaultMessage });

const link = ({ key, to, icon, title, exact = true, special = null, access = null, accessDenied = 'disable' }) => ({
    type: 'link',
    key: key || to,
    to,
    icon,
    title,
    exact,
    special,
    access,
    accessDenied,
});

const dropdown = ({
    key,
    title,
    icon,
    items = [],
    isHeader = false,
    isFullWidth = false,
    useLocale = false,
    content = null,
}) => ({
    type: 'dropdown',
    key,
    title,
    icon,
    items,
    isHeader,
    isFullWidth,
    useLocale,
    content,
});

const group = ({
    key,
    title,
    icon,
    paths = [],
    items = [],
    content = null,
    isHeader = false,
    submenuVariant = null,
    isFullWidth = false,
}) => ({
    type: 'group',
    key,
    title,
    icon,
    paths,
    items,
    content,
    isHeader,
    submenuVariant,
    isFullWidth,
});

const button = ({ key, title, icon, action }) => ({
    type: 'button',
    key,
    title,
    icon,
    action,
});

export function createUserHeaderMenuConfig({ adminPrivileges = false } = {}) {
    const adminPanelLink = adminPrivileges
        ? link({
              key: 'admin-panel',
              to: '/console',
              icon: 'console, command-line',
              title: message('HeaderMenu.list.adminPanel', 'Админ-панель'),
          })
        : null;

    return {
        desktop: {
            leftItems: [
                link({
                    to: '/',
                    icon: 'home',
                    title: message('UserMenu.list.home', 'Главная'),
                }),
                link({
                    to: '/projects',
                    icon: 'file-text',
                    title: message('UserMenu.list.projects', 'Проекты'),
                }),
                dropdown({
                    key: 'transactions',
                    title: message('UserMenu.list.transactions', 'Транзакции'),
                    icon: 'stats-dots',
                    items: [
                        link({
                            to: '/transactions',
                            icon: 'dollar',
                            title: message('UserMenu.title.payments', 'Платежи'),
                        }),
                        link({
                            to: '/payouts',
                            icon: 'file-excel',
                            title: message('UserMenu.title.registries', 'Реестры'),
                        }),
                        link({
                            key: 'mobile-payouts',
                            to: '/mobile-payouts',
                            icon: 'mobile',
                            title: message('UserMenu.title.mobileRegistries', 'Реестры (СБП)'),
                            access: ['ACCESS_SBP_REGISTRY'],
                            exact: false,
                        }),
                    ],
                }),
                link({
                    key: 'statement',
                    to: '/statement',
                    icon: 'file-text',
                    title: message('UserMenu.title.statement', 'Выписка'),
                    access: ['ACCESS_STATEMENT'],
                    exact: false,
                }),
                dropdown({
                    key: 'antifraud',
                    title: message('UserMenu.title.antifraud', 'Антифрод'),
                    icon: 'shield',
                    paths: ['/antifraud-check', '/antifraud-single-check', '/antifraud-log'],
                    items: [
                        link({
                            key: 'antifraud-check',
                            to: '/antifraud-check',
                            icon: 'cloud-upload',
                            title: message('UserMenu.title.antifraudFileCheck', 'Проверка файла'),
                            access: ['ACCESS_ANTIFRAUD_FILE'],
                            exact: false,
                        }),
                        link({
                            key: 'antifraud-single-check',
                            to: '/antifraud-single-check',
                            icon: 'search',
                            title: message('UserMenu.title.antifraudSingleCheck', 'Разовая проверка'),
                            access: ['ACCESS_ANTIFRAUD_SINGLE'],
                            exact: false,
                        }),
                        link({
                            key: 'antifraud-log',
                            to: '/antifraud-log',
                            icon: 'history',
                            title: message('UserMenu.title.antifraudLog', 'Лог проверок'),
                            access: ['ACCESS_ANTIFRAUD_LOG'],
                            exact: false,
                        }),
                    ],
                }),
                dropdown({
                    key: 'finance',
                    title: message('UserMenu.title.finance', 'Финансы'),
                    icon: 'coin-dollar',
                    items: [
                        link({
                            key: 'registries',
                            to: '/registry-upload',
                            icon: 'share',
                            title: message('UserMenu.title.registries', 'Реестры'),
                            access: ['PAYOUT_REGISTRY_PROCESS'],
                            exact: false,
                        }),
                        link({
                            key: 'sbp-registries',
                            to: '/mobile-registry-upload',
                            icon: 'mobile',
                            title: message('UserMenu.title.sbpRegistries', 'Реестры (СБП)'),
                            access: ['ACCESS_SBP_REGISTRY'],
                            exact: false,
                        }),
                        link({
                            key: 'payouts',
                            to: '/pay',
                            icon: 'banknote',
                            title: message('UserMenu.title.payouts', 'Выплаты'),
                            access: ['ACCESS_PAYOUTS'],
                            exact: false,
                        }),
                        link({
                            key: 'sbp-payouts',
                            to: '/sbp-pay',
                            icon: 'mobile',
                            title: message('UserMenu.title.sbpPayouts', 'Выплаты (СБП)'),
                            access: ['ACCESS_SBP_PAYOUT'],
                            exact: false,
                        }),
                        link({
                            key: 'metal',
                            to: '/metal',
                            icon: 'dollar',
                            title: message('UserMenu.title.metal', 'Металл'),
                            access: ['ACCESS_METAL'],
                            accessDenied: 'hide',
                            exact: false,
                        }),
                    ],
                }),
                link({
                    key: 'prepaid',
                    to: '/prepaid',
                    icon: 'credit-card',
                    title: message('UserMenu.title.prepaid', 'Предоплаченные карты'),
                    access: ['PREPAID_CARDS_VIEW'],
                    accessDenied: 'hide',
                    exact: false,
                }),
            ],
            rightItems: [
                ...(adminPanelLink ? [adminPanelLink] : []),
                group({
                    key: 'profile',
                    title: (ctx) => ctx.profileName,
                    icon: 'user',
                    isHeader: true,
                    items: [
                        link({
                            to: '/profile',
                            icon: 'user-tie',
                            title: message('HeaderMenu.list.account', 'Мой профиль'),
                        }),
                        link({
                            to: '/secret-chats',
                            icon: 'lock',
                            title: message('HeaderMenu.list.secretChats', 'Chats'),
                            // Счётчик непрочитанного — тем же механизмом, что и остальные метки.
                            badge: (ctx) => (Number(ctx.secretChatUnread || 0) > 0 ? String(ctx.secretChatUnread) : null),
                        }),
                        button({
                            key: 'logout',
                            title: message('HeaderMenu.list.logout', 'Выход'),
                            icon: 'stand-by',
                            action: 'logout',
                        }),
                    ],
                }),
            ],
        },
        drawer: {
            sections: [
                link({
                    to: '/',
                    icon: 'home',
                    title: message('UserMenu.list.home', 'Главная'),
                }),
                link({
                    to: '/projects',
                    icon: 'file-text',
                    title: message('UserMenu.list.projects', 'Проекты'),
                }),
                group({
                    key: 'transactions',
                    title: message('UserMenu.list.transactions', 'Транзакции'),
                    icon: 'stats-dots',
                    paths: ['/transactions', '/payouts', '/mobile-payouts'],
                    items: [
                        link({
                            to: '/transactions',
                            icon: 'dollar',
                            title: message('UserMenu.title.payments', 'Платежи'),
                        }),
                        link({
                            to: '/payouts',
                            icon: 'file-excel',
                            title: message('UserMenu.title.registries', 'Реестры'),
                        }),
                        link({
                            key: 'mobile-payouts',
                            to: '/mobile-payouts',
                            icon: 'mobile',
                            title: message('UserMenu.title.mobileRegistries', 'Реестры (СБП)'),
                            access: ['ACCESS_SBP_REGISTRY'],
                            exact: false,
                        }),
                    ],
                }),
                link({
                    key: 'statement',
                    to: '/statement',
                    icon: 'file-text',
                    title: message('UserMenu.title.statement', 'Выписка'),
                    access: ['ACCESS_STATEMENT'],
                    exact: false,
                }),
                group({
                    key: 'antifraud',
                    title: message('UserMenu.title.antifraud', 'Антифрод'),
                    icon: 'shield',
                    paths: ['/antifraud-check', '/antifraud-single-check', '/antifraud-log'],
                    items: [
                        link({
                            key: 'antifraud-check',
                            to: '/antifraud-check',
                            icon: 'cloud-upload',
                            title: message('UserMenu.title.antifraudFileCheck', 'Проверка файла'),
                            access: ['ACCESS_ANTIFRAUD_FILE'],
                            exact: false,
                        }),
                        link({
                            key: 'antifraud-single-check',
                            to: '/antifraud-single-check',
                            icon: 'search',
                            title: message('UserMenu.title.antifraudSingleCheck', 'Разовая проверка'),
                            access: ['ACCESS_ANTIFRAUD_SINGLE'],
                            exact: false,
                        }),
                        link({
                            key: 'antifraud-log',
                            to: '/antifraud-log',
                            icon: 'history',
                            title: message('UserMenu.title.antifraudLog', 'Лог проверок'),
                            access: ['ACCESS_ANTIFRAUD_LOG'],
                            exact: false,
                        }),
                    ],
                }),
                group({
                    key: 'finance',
                    title: message('UserMenu.title.finance', 'Финансы'),
                    icon: 'coin-dollar',
                    paths: ['/registry-upload', '/mobile-registry-upload', '/pay', '/sbp-pay', '/metal'],
                    items: [
                        link({
                            key: 'registries',
                            to: '/registry-upload',
                            icon: 'share',
                            title: message('UserMenu.title.registries', 'Реестры'),
                            access: ['PAYOUT_REGISTRY_PROCESS'],
                            exact: false,
                        }),
                        link({
                            key: 'sbp-registries',
                            to: '/mobile-registry-upload',
                            icon: 'mobile',
                            title: message('UserMenu.title.sbpRegistries', 'Реестры (СБП)'),
                            access: ['ACCESS_SBP_REGISTRY'],
                            exact: false,
                        }),
                        link({
                            key: 'payouts',
                            to: '/pay',
                            icon: 'banknote',
                            title: message('UserMenu.title.payouts', 'Выплаты'),
                            access: ['ACCESS_PAYOUTS'],
                            exact: false,
                        }),
                        link({
                            key: 'sbp-payouts',
                            to: '/sbp-pay',
                            icon: 'mobile',
                            title: message('UserMenu.title.sbpPayouts', 'Выплаты (СБП)'),
                            access: ['ACCESS_SBP_PAYOUT'],
                            exact: false,
                        }),
                        link({
                            key: 'metal',
                            to: '/metal',
                            icon: 'dollar',
                            title: message('UserMenu.title.metal', 'Металл'),
                            access: ['ACCESS_METAL'],
                            accessDenied: 'hide',
                            exact: false,
                        }),
                    ],
                }),
                link({
                    key: 'prepaid',
                    to: '/prepaid',
                    icon: 'credit-card',
                    title: message('UserMenu.title.prepaid', 'Предоплаченные карты'),
                    access: ['PREPAID_CARDS_VIEW'],
                    accessDenied: 'hide',
                    exact: false,
                }),
            ],
            footerTitle: (ctx) => ctx.profileName,
            footerItems: [
                ...(adminPanelLink ? [adminPanelLink] : []),
                link({
                    to: '/profile',
                    icon: 'user-tie',
                    title: message('HeaderMenu.list.account', 'Мой профиль'),
                }),
                link({
                    to: '/secret-chats',
                    icon: 'lock',
                    title: message('HeaderMenu.list.secretChats', 'Chats'),
                    // Счётчик непрочитанного — тем же механизмом, что и остальные метки.
                    badge: (ctx) => (Number(ctx.secretChatUnread || 0) > 0 ? String(ctx.secretChatUnread) : null),
                }),
                button({
                    key: 'logout',
                    title: message('HeaderMenu.list.logout', 'Выход'),
                    icon: 'stand-by',
                    action: 'logout',
                }),
            ],
        },
    };
}

export function createAdminHeaderMenuConfig() {
    const homeLink = link({
        key: 'client-home',
        to: '/',
        icon: 'home',
        title: message('HeaderMenu.list.clientHome', 'На главную'),
    });

    return {
        desktop: {
            leftItems: [
                link({
                    to: '/console',
                    icon: 'console, command-line',
                    title: message('AdministrationMenu.title.console', 'Админ-панель'),
                }),
                link({
                    to: '/transaction-audit',
                    icon: 'time',
                    title: message('AdministrationMenu.title.transactionAudit', 'Аудит транзакций'),
                }),
                dropdown({
                    key: 'settings',
                    title: message('AdministrationMenu.list.settings', 'Настройки'),
                    icon: 'user-group',
                    paths: ['/accounts-control', '/projects-control', '/terminals-control', '/profile-control'],
                    items: [
                        link({
                            to: '/accounts-control',
                            icon: 'user-tie',
                            title: message('AdministrationMenu.title.accountsList', 'Сотрудники'),
                        }),
                        link({
                            to: '/projects-control',
                            icon: 'store-front',
                            title: message('AdministrationMenu.title.projectsList', 'Проекты'),
                        }),
                        link({
                            to: '/terminals-control',
                            icon: 'calculator',
                            title: message('AdministrationMenu.title.terminalsList', 'Терминалы'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'finance',
                    title: message('AdministrationMenu.list.finance', 'Финансы'),
                    icon: 'dollar',
                    paths: ['/settlements', '/deposits', '/crm-payout-requests', '/crm-sbp-payout-requests', '/reconciliation'],
                    items: [
                        link({
                            to: '/settlements',
                            icon: 'calculator',
                            title: message('AdministrationMenu.title.settlements', 'Расчёты'),
                        }),
                        link({
                            to: '/deposits',
                            icon: 'wallet',
                            title: message('AdministrationMenu.title.deposits', 'Депозиты'),
                        }),
                        link({
                            to: '/crm-payout-requests',
                            icon: 'banknote',
                            title: message('AdministrationMenu.title.crmPayoutRequests', 'CRM-заявки'),
                        }),
                        link({
                            to: '/crm-sbp-payout-requests',
                            icon: 'banknote',
                            title: message('AdministrationMenu.title.crmSbpPayoutRequests', 'CRM-заявки СБП'),
                        }),
                        link({
                            to: '/reconciliation',
                            icon: 'stats-dots',
                            title: message('AdministrationMenu.title.reconciliation', 'Сверка'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'analytics',
                    title: message('AdministrationMenu.list.analytics', 'Аналитика'),
                    icon: 'stats-dots',
                    paths: ['/traffic-monitoring', '/admin-audit', '/antifraud', '/payout-holds', '/report-constructor'],
                    items: [
                        link({
                            to: '/traffic-monitoring',
                            icon: 'stats-dots',
                            title: message('AdministrationMenu.title.trafficMonitoring', 'Мониторинг трафика'),
                        }),
                        link({
                            to: '/admin-audit',
                            icon: 'history',
                            title: message('AdministrationMenu.title.adminAudit', 'Журнал действий'),
                        }),
                        link({
                            to: '/antifraud',
                            icon: 'lock',
                            title: message('AdministrationMenu.title.antifraud', 'Антифрод'),
                        }),
                        link({
                            to: '/payout-holds',
                            icon: 'warning',
                            title: message('AdministrationMenu.title.payoutHolds', 'Остановленные выплаты'),
                        }),
                        link({
                            to: '/report-constructor',
                            icon: 'construction',
                            title: message('AdministrationMenu.title.constructor', 'Конструктор'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'support',
                    title: message('AdministrationMenu.list.support', 'Поддержка'),
                    icon: 'message',
                    paths: ['/disputes', '/admin/tickets', '/api-console'],
                    items: [
                        link({
                            to: '/disputes',
                            icon: 'warning',
                            title: message('AdministrationMenu.title.disputes', 'Диспуты'),
                        }),
                        link({
                            to: '/admin/tickets',
                            icon: 'message',
                            title: message('AdministrationMenu.title.tickets', 'Обращения'),
                        }),
                        link({
                            to: '/admin/secret-chats',
                            icon: 'lock',
                            title: message('AdministrationMenu.title.secretChats', 'Секретные чаты'),
                            // Счётчик непрочитанного — тем же механизмом, что и остальные метки.
                            badge: (ctx) => (Number(ctx.secretChatUnread || 0) > 0 ? String(ctx.secretChatUnread) : null),
                        }),
                    ],
                }),
            ],
            rightItems: [
                homeLink,
                group({
                    key: 'profile',
                    title: (ctx) => ctx.profileName,
                    icon: 'user',
                    isHeader: true,
                    items: [
                        link({
                            to: '/profile-control',
                            icon: 'user-tie',
                            title: message('HeaderMenu.list.account', 'Мой профиль'),
                        }),
                        link({
                            to: '/api-console',
                            icon: 'code',
                            title: message('HeaderMenu.list.apiconsole', 'API консоль'),
                        }),
                        button({
                            key: 'logout',
                            title: message('HeaderMenu.list.logout', 'Выход'),
                            icon: 'stand-by',
                            action: 'logout',
                        }),
                    ],
                }),
            ],
        },
        drawer: {
            sections: [
                link({
                    to: '/console',
                    icon: 'console, command-line',
                    title: message('AdministrationMenu.title.console', 'Админ-панель'),
                }),
                link({
                    to: '/transaction-audit',
                    icon: 'time',
                    title: message('AdministrationMenu.title.transactionAudit', 'Аудит транзакций'),
                }),
                dropdown({
                    key: 'settings',
                    title: message('AdministrationMenu.list.settings', 'Настройки'),
                    icon: 'user-group',
                    items: [
                        link({
                            to: '/accounts-control',
                            icon: 'user-tie',
                            title: message('AdministrationMenu.title.accountsList', 'Сотрудники'),
                        }),
                        link({
                            to: '/projects-control',
                            icon: 'store-front',
                            title: message('AdministrationMenu.title.projectsList', 'Проекты'),
                        }),
                        link({
                            to: '/terminals-control',
                            icon: 'calculator',
                            title: message('AdministrationMenu.title.terminalsList', 'Терминалы'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'finance',
                    title: message('AdministrationMenu.list.finance', 'Финансы'),
                    icon: 'dollar',
                    items: [
                        link({
                            to: '/settlements',
                            icon: 'calculator',
                            title: message('AdministrationMenu.title.settlements', 'Расчёты'),
                        }),
                        link({
                            to: '/deposits',
                            icon: 'wallet',
                            title: message('AdministrationMenu.title.deposits', 'Депозиты'),
                        }),
                        link({
                            to: '/crm-payout-requests',
                            icon: 'banknote',
                            title: message('AdministrationMenu.title.crmPayoutRequests', 'CRM-заявки'),
                        }),
                        link({
                            to: '/crm-sbp-payout-requests',
                            icon: 'banknote',
                            title: message('AdministrationMenu.title.crmSbpPayoutRequests', 'CRM-заявки СБП'),
                        }),
                        link({
                            to: '/reconciliation',
                            icon: 'stats-dots',
                            title: message('AdministrationMenu.title.reconciliation', 'Сверка'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'analytics',
                    title: message('AdministrationMenu.list.analytics', 'Аналитика'),
                    icon: 'stats-dots',
                    items: [
                        link({
                            to: '/traffic-monitoring',
                            icon: 'stats-dots',
                            title: message('AdministrationMenu.title.trafficMonitoring', 'Мониторинг трафика'),
                        }),
                        link({
                            to: '/admin-audit',
                            icon: 'history',
                            title: message('AdministrationMenu.title.adminAudit', 'Журнал действий'),
                        }),
                        link({
                            to: '/antifraud',
                            icon: 'lock',
                            title: message('AdministrationMenu.title.antifraud', 'Антифрод'),
                        }),
                        link({
                            to: '/payout-holds',
                            icon: 'warning',
                            title: message('AdministrationMenu.title.payoutHolds', 'Остановленные выплаты'),
                        }),
                        link({
                            to: '/report-constructor',
                            icon: 'construction',
                            title: message('AdministrationMenu.title.constructor', 'Конструктор'),
                        }),
                    ],
                }),
                dropdown({
                    key: 'support',
                    title: message('AdministrationMenu.list.support', 'Поддержка'),
                    icon: 'message',
                    items: [
                        link({
                            to: '/disputes',
                            icon: 'warning',
                            title: message('AdministrationMenu.title.disputes', 'Диспуты'),
                        }),
                        link({
                            to: '/admin/tickets',
                            icon: 'message',
                            title: message('AdministrationMenu.title.tickets', 'Обращения'),
                        }),
                        link({
                            to: '/admin/secret-chats',
                            icon: 'lock',
                            title: message('AdministrationMenu.title.secretChats', 'Секретные чаты'),
                            // Счётчик непрочитанного — тем же механизмом, что и остальные метки.
                            badge: (ctx) => (Number(ctx.secretChatUnread || 0) > 0 ? String(ctx.secretChatUnread) : null),
                        }),
                    ],
                }),
            ],
            footerItems: [
                homeLink,
                link({
                    to: '/profile-control',
                    icon: 'user-tie',
                    title: message('HeaderMenu.list.account', 'Мой профиль'),
                }),
                link({
                    to: '/api-console',
                    icon: 'code',
                    title: message('HeaderMenu.list.apiconsole', 'API консоль'),
                }),
                button({
                    key: 'logout',
                    title: message('HeaderMenu.list.logout', 'Выход'),
                    icon: 'stand-by',
                    action: 'logout',
                }),
            ],
        },
    };
}

export function createDefaultHeaderMenuConfig({ adminPrivileges } = {}) {
    return adminPrivileges ? createAdminHeaderMenuConfig() : createUserHeaderMenuConfig();
}



// WEBPACK FOOTER //
// ./src/Components/HeaderMenu/menuConfig.js